import { type ReactNode, useEffect, useState } from "react";
import { useRiskSuggestions } from "@/api/merchant-risks";
import { Button } from "@/components/base/buttons/button";
import { CloseButton } from "@/components/base/buttons/close-button";
import { RadioButton, RadioGroup } from "@/components/base/radio-buttons/radio-buttons";
import { AutoTextarea } from "@/components/ui/auto-textarea";
import { Popover, PopoverAnchor, PopoverContent } from "@/components/ui/popover";
import { cx } from "@/utils/cx";
import { RiskMonitoringTip, useRiskMonitoring } from "./RiskMonitoring";

export const OTHER_DISMISS_REASON = "Other";
const ALLOWED_FOR_BRAND_REASON = "Allowed for our brand";

export const DISMISS_REASONS = [
  "Misidentified content",
  "Out of context",
  ALLOWED_FOR_BRAND_REASON,
  "Already handled with the creator",
  OTHER_DISMISS_REASON,
];

export type DismissReason = { reason: string; note: string };

type DismissReasonPopoverProps = {
  /** The ✕ button the popover opens beside; it opens it through `onOpenChange`. */
  children: ReactNode;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  riskId?: string | null;
  riskName: string;
  /** The reason already given, when the flag was dismissed before; the popover then edits it. */
  saved?: DismissReason;
  onSubmit: (dismissal: DismissReason) => void;
};

/**
 * Asks why a flag is being dismissed. "Allowed for our brand" offers to stop monitoring the flag's
 * own risk; an "Other" note is matched against the catalog, and a matching risk that isn't
 * monitored is offered to be switched on.
 */
export const DismissReasonPopover = ({
  children,
  open,
  onOpenChange,
  riskId,
  riskName,
  saved,
  onSubmit,
}: DismissReasonPopoverProps) => {
  const [reason, setReason] = useState<string | undefined>();
  const [note, setNote] = useState("");
  const { itemFor, isMonitored } = useRiskMonitoring();
  const suggestions = useRiskSuggestions(reason === OTHER_DISMISS_REASON ? note : "");

  // Starts from the saved reason each time it opens, not on every render while open.
  useEffect(() => {
    if (!open) return;
    setReason(saved?.reason);
    setNote(saved?.note ?? "");
  }, [open]);

  const ownItem = riskId ? itemFor(riskId) : undefined;
  const ownRiskMonitored = !!riskId && isMonitored(riskId);
  const suggested = suggestions[0];
  const suggestedItem = suggested ? itemFor(suggested.riskId) : undefined;
  const suggestUnmonitored =
    reason === OTHER_DISMISS_REASON &&
    !!suggested &&
    !!suggestedItem &&
    suggestedItem.key !== ownItem?.key &&
    !isMonitored(suggested.riskId);

  const submit = () => {
    if (!reason) return;
    onSubmit({ reason, note: reason === OTHER_DISMISS_REASON ? note.trim() : "" });
    onOpenChange(false);
  };

  return (
    <Popover open={open} onOpenChange={onOpenChange}>
      <PopoverAnchor asChild>
        <span className="inline-flex">{children}</span>
      </PopoverAnchor>
      <PopoverContent side="left" align="start" className="flex w-[460px] flex-col gap-4 rounded-xl p-5 shadow-lg">
        <div className="flex items-start justify-between gap-4">
          <div className="flex flex-col gap-0.5">
            <span className="text-md font-semibold text-primary">
              {saved ? "Why you dismissed this flag" : "Why are you dismissing this flag?"}
            </span>
            <span className="text-sm text-tertiary">Risk: {riskName}</span>
          </div>
          <CloseButton size="xs" label="Close" onPress={() => onOpenChange(false)} />
        </div>

        <RadioGroup aria-label="Dismiss reason" value={reason ?? null} onChange={setReason} className="gap-1">
          {DISMISS_REASONS.map((option) => (
            <RadioButton
              key={option}
              value={option}
              label={option}
              className={({ isSelected }) =>
                cx("items-center rounded-lg px-3 py-2", isSelected && "bg-brand-50 ring-1 ring-brand ring-inset")
              }
            />
          ))}
        </RadioGroup>

        {reason === OTHER_DISMISS_REASON && (
          <AutoTextarea
            minRows={1}
            maxRows={6}
            placeholder="What did the AI get wrong?"
            value={note}
            onChange={(event) => setNote(event.target.value)}
          />
        )}

        {reason === ALLOWED_FOR_BRAND_REASON && riskId && ownRiskMonitored && (
          <RiskMonitoringTip riskId={riskId} riskName={ownItem?.name ?? riskName} monitored />
        )}
        {suggestUnmonitored && suggested && (
          <RiskMonitoringTip riskId={suggested.riskId} riskName={suggested.readableName} monitored={false} />
        )}

        <div className="flex justify-end gap-3 border-t border-secondary pt-4">
          <Button color="secondary" size="sm" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button color="primary" size="sm" isDisabled={!reason} onClick={submit}>
            {saved ? "Save" : "Dismiss flag"}
          </Button>
        </div>
      </PopoverContent>
    </Popover>
  );
};
